import { useMemo } from 'react';
import { useAddOmnisendEventMutation, useGetProductPricesQuery } from '../../../features/quizApiSlice';
import {
  useAddOmega3ToSubscriptionMutation,
  useGetUserSubscriptionQuery,
} from '../../../features/customerApiSlice';
import useMeta from '../../../hooks/useMeta';
import { waitForTracking } from '../../../utils/tracking';
import { OMEGA3_DURATION_TO_PLAN_KEY, OMEGA3_PLAN_KEYS, OMEGA3_PRODUCT_TYPE } from '../data/omega3Data';

const PLAN_ORDER = [
  OMEGA3_PLAN_KEYS.MONTHLY_SUBSCRIPTION,
  OMEGA3_PLAN_KEYS.QUARTERLY_SUBSCRIPTION,
  OMEGA3_PLAN_KEYS.ONE_MONTH_ONE_OFF,
  OMEGA3_PLAN_KEYS.THREE_MONTH_ONE_OFF,
];

const SUBSCRIPTION_DISCOUNT_BY_PLAN = {
  [OMEGA3_PLAN_KEYS.MONTHLY_SUBSCRIPTION]: 0.1,
  [OMEGA3_PLAN_KEYS.QUARTERLY_SUBSCRIPTION]: 0.2,
};

const TARGET_AMOUNT_BY_PLAN = {
  [OMEGA3_PLAN_KEYS.MONTHLY_SUBSCRIPTION]: 24.3,
  [OMEGA3_PLAN_KEYS.QUARTERLY_SUBSCRIPTION]: 64.8,
  [OMEGA3_PLAN_KEYS.ONE_MONTH_ONE_OFF]: 27,
  [OMEGA3_PLAN_KEYS.THREE_MONTH_ONE_OFF]: 81,
};

function applySubscriptionDiscount(planKey, amount) {
  const discount = SUBSCRIPTION_DISCOUNT_BY_PLAN[planKey] || 0;
  if (!discount) return amount;
  return Math.round(amount * (1 - discount) * 10) / 10;
}

const PLAN_META = {
  [OMEGA3_PLAN_KEYS.MONTHLY_SUBSCRIPTION]: {
    titleKey: 'Omega3_Plan_Monthly_Title',
    badgeKey: 'Omega3_Plan_Monthly_Badge',
    detailKeys: [
      'Omega3_Plan_Monthly_Detail_1',
      'Omega3_Plan_Monthly_Detail_2',
      'Omega3_Plan_Monthly_Detail_3',
    ],
    recurring: true,
    quantityLabelKey: 'Omega3_Plan_Monthly_Quantity',
    ctaLabelKey: 'Omega3_Cta_Subscribe',
    fallbackAmount: 27,
  },
  [OMEGA3_PLAN_KEYS.QUARTERLY_SUBSCRIPTION]: {
    titleKey: 'Omega3_Plan_Quarterly_Title',
    badgeKey: 'Omega3_Plan_Quarterly_Badge',
    detailKeys: [
      'Omega3_Plan_Quarterly_Detail_1',
      'Omega3_Plan_Quarterly_Detail_2',
      'Omega3_Plan_Quarterly_Detail_3',
    ],
    recurring: true,
    quantityLabelKey: 'Omega3_Plan_Quarterly_Quantity',
    ctaLabelKey: 'Omega3_Cta_Subscribe',
    fallbackAmount: 81,
  },
  [OMEGA3_PLAN_KEYS.ONE_MONTH_ONE_OFF]: {
    titleKey: 'Omega3_Plan_OneOffMonthly_Title',
    badgeKey: 'Omega3_Plan_OneOffMonthly_Badge',
    detailKeys: [
      'Omega3_Plan_OneOffMonthly_Detail_1',
      'Omega3_Plan_OneOffMonthly_Detail_2',
      'Omega3_Plan_OneOffMonthly_Detail_3',
    ],
    recurring: false,
    quantityLabelKey: 'Omega3_Plan_OneOffMonthly_Quantity',
    ctaLabelKey: 'Omega3_Cta_BuyNow',
    fallbackAmount: 27,
  },
  [OMEGA3_PLAN_KEYS.THREE_MONTH_ONE_OFF]: {
    titleKey: 'Omega3_Plan_OneOffQuarterly_Title',
    badgeKey: 'Omega3_Plan_OneOffQuarterly_Badge',
    detailKeys: [
      'Omega3_Plan_OneOffQuarterly_Detail_1',
      'Omega3_Plan_OneOffQuarterly_Detail_2',
      'Omega3_Plan_OneOffQuarterly_Detail_3',
    ],
    recurring: false,
    quantityLabelKey: 'Omega3_Plan_OneOffQuarterly_Quantity',
    ctaLabelKey: 'Omega3_Cta_BuyNow',
    fallbackAmount: 81,
  },
};

const USER_SUB_TO_OMEGA_PLAN = {
  3: OMEGA3_PLAN_KEYS.MONTHLY_SUBSCRIPTION,
  6: OMEGA3_PLAN_KEYS.QUARTERLY_SUBSCRIPTION,
};

const ACTIVE_SUBSCRIPTION_STATUSES = new Set(['active', 'trialing', 'paused']);

function isActiveSubscription(subscription) {
  return ACTIVE_SUBSCRIPTION_STATUSES.has(String(subscription?.status || '').toLowerCase());
}

function buildOmega3OmnisendSubscription(plan) {
  return {
    title: plan?.titleKey || 'Omega-3',
    description: plan?.duration || 'Omega-3',
    duration: plan?.duration || null,
    recurring: Boolean(plan?.recurring),
    product_type: OMEGA3_PRODUCT_TYPE,
  };
}

export default function useOmega3Addon({ subscriptionOverride = null } = {}) {
  const { metaEvent } = useMeta();
  const {
    data: pricingData,
    isLoading: isPricingLoading,
    isFetching: isPricingFetching,
  } = useGetProductPricesQuery({ product_type: OMEGA3_PRODUCT_TYPE });
  const [addOmnisendEventMutation] = useAddOmnisendEventMutation();
  const {
    data: userSubscription,
    isLoading: isUserSubLoading,
    isFetching: isUserSubFetching,
  } = useGetUserSubscriptionQuery();
  const [addOmega3ToSubscription, { isLoading: isAdding }] = useAddOmega3ToSubscriptionMutation();

  const allPlans = useMemo(() => {
    const rows = Array.isArray(pricingData?.data) ? pricingData.data : [];
    const map = rows.reduce((acc, row) => {
      if (!row?.payment_link) return acc;

      const planKey = OMEGA3_DURATION_TO_PLAN_KEY[row.duration];
      if (!planKey) return acc;

      const quantity =
        planKey === OMEGA3_PLAN_KEYS.QUARTERLY_SUBSCRIPTION ||
        planKey === OMEGA3_PLAN_KEYS.THREE_MONTH_ONE_OFF
          ? 3
          : 1;
      const meta = PLAN_META[planKey];
      const baseAmount = Number(row.amount || meta.fallbackAmount || 0);
      const targetAmount = TARGET_AMOUNT_BY_PLAN[planKey] ?? applySubscriptionDiscount(planKey, baseAmount);
      const amount = targetAmount;
      const amountDiff = Math.abs(baseAmount - targetAmount);

      const candidatePlan = {
        ...meta,
        id: planKey,
        amount,
        currency: 'EUR',
        duration: row.duration,
        quantity,
        productType: row.product_type,
        paymentLink: row.payment_link,
        perMonthAmount: quantity > 1 ? amount / quantity : amount,
      };

      if (!acc[planKey]) {
        acc[planKey] = {
          ...candidatePlan,
          _sourceAmount: baseAmount,
          _amountDiff: amountDiff,
        };
        return acc;
      }

      const currentDiff = acc[planKey]._amountDiff ?? Number.POSITIVE_INFINITY;
      const currentSourceAmount = acc[planKey]._sourceAmount ?? Number.POSITIVE_INFINITY;

      if (amountDiff < currentDiff || (amountDiff === currentDiff && baseAmount < currentSourceAmount)) {
        acc[planKey] = {
          ...candidatePlan,
          _sourceAmount: baseAmount,
          _amountDiff: amountDiff,
        };
      }

      return acc;
    }, {});

    return PLAN_ORDER.map((key) => {
      const matchedPlan = map[key];
      if (matchedPlan) {
        const { _sourceAmount, _amountDiff, ...cleanPlan } = matchedPlan;
        return cleanPlan;
      }

      return {
        ...PLAN_META[key],
        id: key,
        amount: TARGET_AMOUNT_BY_PLAN[key] ?? applySubscriptionDiscount(key, PLAN_META[key].fallbackAmount),
        currency: 'EUR',
        quantity: key.includes('quarterly') ? 3 : 1,
        productType: OMEGA3_PRODUCT_TYPE,
        paymentLink: null,
        perMonthAmount: key.includes('quarterly')
          ? (TARGET_AMOUNT_BY_PLAN[key] ?? applySubscriptionDiscount(key, PLAN_META[key].fallbackAmount)) / 3
          : (TARGET_AMOUNT_BY_PLAN[key] ?? applySubscriptionDiscount(key, PLAN_META[key].fallbackAmount)),
      };
    });
  }, [pricingData]);
  const subscriptions = useMemo(() => {
    const rows = userSubscription?.subscription;
    return Array.isArray(rows) ? rows : [];
  }, [userSubscription]);

  const overrideRiisePlan = useMemo(() => {
    if (subscriptionOverride?.product_type !== 'riise_formula') return null;
    return isActiveSubscription(subscriptionOverride) ? subscriptionOverride : null;
  }, [subscriptionOverride]);

  const latestRiisePlan = useMemo(() => {
    if (overrideRiisePlan) return overrideRiisePlan;

    return (
      subscriptions.find(
        (subscription) => subscription?.product_type === 'riise_formula' && isActiveSubscription(subscription),
      ) || null
    );
  }, [overrideRiisePlan, subscriptions]);

  const hasAnyActiveSubscription = useMemo(() => {
    if (overrideRiisePlan) return true;
    return subscriptions.some((subscription) => isActiveSubscription(subscription));
  }, [overrideRiisePlan, subscriptions]);

  const currentDuration = Number.parseInt(String(latestRiisePlan?.duration ?? ''), 10);
  const matchedPlanKey = USER_SUB_TO_OMEGA_PLAN[currentDuration] || null;
  const selectedPlanKey = matchedPlanKey;

  const selectedPlan = useMemo(() => {
    if (!matchedPlanKey) return null;
    const matched = allPlans.find((plan) => plan.id === matchedPlanKey) || null;
    if (!matched) return null;

    const comparePlan = allPlans.find(
      (plan) => !plan.recurring && Number(plan.quantity) === Number(matched.quantity),
    );

    return {
      ...matched,
      compareBaseAmount: comparePlan?.amount ?? null,
    };
  }, [allPlans, matchedPlanKey]);

  const plans = selectedPlan ? [selectedPlan] : [];
  const hasActiveSubscription = Boolean(latestRiisePlan);

  const addToSubscription = async () => {
    if (!selectedPlan) {
      return { success: false, message: 'missing_required_data' };
    }

    const email = localStorage.getItem('email');

    await waitForTracking([
      metaEvent('AddToCart'),
      metaEvent('InitiateCheckout'),
      email
        ? addOmnisendEventMutation({
            email,
            url: typeof window !== 'undefined' ? window.location.href : '',
            subscribtion: buildOmega3OmnisendSubscription(selectedPlan),
            price: selectedPlan.amount,
          })
            .unwrap()
            .catch((error) => console.log('Error event ', error))
        : null,
    ]);

    return addOmega3ToSubscription({
      product_type: OMEGA3_PRODUCT_TYPE,
      duration: selectedPlan.duration,
    }).unwrap();
  };

  return {
    isLoading: isPricingLoading || isPricingFetching || isUserSubLoading || isUserSubFetching || isAdding,
    plans,
    selectedPlan,
    selectedPlanKey,
    setSelectedPlanKey: () => {},
    addToSubscription,
    userSubscription,
    hasMatchingPlan: Boolean(selectedPlan),
    hasActiveStripeSubscription: hasActiveSubscription,
    hasAnyActiveSubscription,
  };
}
